import { AttrPath, CategorieUitstroomniveauVSO, TussentijdseUitstroomVSO, VergelijkingUitstroomniveauVSO } from '@cumlaude/metadata';
import { DashboardOptionParamWithValues } from '@cumlaude/shared-dashboard-registry';
import { range } from 'lodash-es';

export enum AanwezigheidUitsplitsing {
	LEERLING = 'Per leerling',
	KLAS = 'Per klas',
	VAK = 'Per vak',
}

export enum AfwijkingPercentielWeergave {
	PERCENTIEL = 'Percentiel',
	AFWIJKING = 'Afwijking',
}

export enum CijferColumn {
	Cijfer = 'Cijfer',
	GemAndereVakken = 'Gem. andere vakken',
	Sectiegemiddelde = 'Sectiegemiddelde',
	GemAantalToetsen = 'Gemiddeld aantal toetsen',
	CijferVorigSchooljaar = 'Cijfer vorig schooljaar',
	Tekortpunten = 'Tekortpunten',
	GemTekortpunten = 'Gem. tekortpunten',
	Leerlingen = 'Leerlingen',
	PercOnvoldoende = '% onvoldoende',
	Mediaan = 'Mediaan',
	PlaatsingAdvies = 'Plaatsing/advies',
	AantalCijfers = 'Aantal cijfers',
	PercAfwezigheidOngeoorloofd = '% afwezigheid ongeoorloofd',
	PercAfwezigheidGeoorloofd = '% afwezigheid geoorloofd',
}

export enum CijferkolomKeuze {
	ALLE = '<Alle>',
	ADVIES = 'Advies',
	TOETS = 'Toets',
	GEMIDDELDE = 'Gemiddelde',
}

export enum CijfersSeCeWeergave {
	VERSCHIL = 'SE-CE verschil',
	SE = 'SE-cijfer',
	CE = 'CE-cijfer',
}

export const COHORTRENDEMENT_TYPES = ['Cohortrendement tot', 'Cohortrendement vanaf', 'Examenrendement', 'Opstroomrendement'] as const;

export type CohortrendementType = (typeof COHORTRENDEMENT_TYPES)[number];

export const COHORTRENDEMENT_WEERGAVEN = ['Cohortrendement', 'Doublures', 'Niveauverschil'] as const;

export type CohortrendementWeergave = (typeof COHORTRENDEMENT_WEERGAVEN)[number];

export enum Toggle {
	ON = '1',
	OFF = '0',
}

export const INDICATOR_OVER_JAREN = [1, 2, 3];

export const THRESHOLD_VALUES = [0, 5, 10, 15, 20, 30, 50, 75, 100, 200];

export const TEKORTPUNTEN_VALUES = range(0, 6);

export const KENMERKEN: AttrPath[] = [
	['ds_is_apcg_van'],
	['ds_fk_ilt_van', 'ilt_is_lwoo'],
	['ds_fk_ilt_van', 'ilt_is_vavo'],
	['ds_nm_instroom_in_schooljaar'],
	['ds_nm_uitstroom_in_schooljaar'],
	['ds_is_examenopstroom_van'],
];

export const VAKORDER_ACTIVE = ['Tekortpunten', 'Volgnummer', 'Alfabetisch'];

export const VAKORDER_DIRECTIONS = ['asc', 'desc'];

export enum DashboardVariant {
	ACTUEEL = 'Actueel',
	HISTORIE = 'Historie',
}

export enum DashboardAspect {
	VERDELING = 'Verdeling',
	GEMIDDELDE = 'Gemiddelde',
}

export enum DoorstroomWeergave {
	SUCCESVOL = 'Succesvol',
	NIET_SUCCESVOL = 'Niet succesvol',
	BEVORDERD = 'Bevorderd',
	NIET_BEVORDERD = 'Niet bevorderd',
	OPSTROOM = 'Opstroom',
	AFSTROOM = 'Afstroom',
}

export enum Dossier {
	VOORTGANG = 'Voortgang',
	EXAMEN = 'Examendossier',
}

export enum Eenheid {
	AANTAL = 'Aantal',
	PERCENTAGE = 'Percentage',
}
export enum Interval {
	SCHOOLJAAR = 'Schooljaar',
	MAAND = 'Maand',
	WEEK = 'Week',
}

export enum Tijdseenheid {
	UREN = 'Lesuren',
	TIJD = 'Lestijd',
	DAGEN = 'Lesdagen',
	UREN_PERCENTAGE = 'Lesuren (percentage)',
	TIJD_PERCENTAGE = 'Lestijd (percentage)',
}

export enum VerschilBeoogd {
	ABSOLUUT = 'Absoluut verschil',
	VERSCHIL = 'Verschil',
	BEOOGD = 'Beoogd niveau',
	BEHAALD = 'Behaald niveau',
}

/** De enums achter de opties in `DASHBOARD_OPTIONS`; `FilterPanelComponent` vergelijkt ze in dev-mode met de registry. */
export const OPTION_ENUMS: Record<DashboardOptionParamWithValues, Record<string, string>> = {
	variant: DashboardVariant,
	eenheid: Eenheid,
	aanwezigheiduitsplitsing: AanwezigheidUitsplitsing,
	cijfertype: CijferkolomKeuze,
	aspect: DashboardAspect,
	afwijkingpercentiel: AfwijkingPercentielWeergave,
	'brin-overgang': Toggle,
	categorieuitstroomniveauvso: CategorieUitstroomniveauVSO,
	cijfersseceweergave: CijfersSeCeWeergave,
	cohortrendementtype: valuesOf(COHORTRENDEMENT_TYPES),
	cohortrendementweergave: valuesOf(COHORTRENDEMENT_WEERGAVEN),
	doorstroomweergave: DoorstroomWeergave,
	dossier: Dossier,
	'indicator-over': valuesOf(INDICATOR_OVER_JAREN),
	interval: Interval,
	kenmerk: valuesOf(KENMERKEN.map((attrPath) => attrPath.join('.'))),
	'or-uitsluiten': Toggle,
	tekortpunten: valuesOf(TEKORTPUNTEN_VALUES),
	threshold: valuesOf(THRESHOLD_VALUES),
	tijdseenheid: Tijdseenheid,
	tussentijdseuitstroomvso: TussentijdseUitstroomVSO,
	uitstroomniveauvso: VergelijkingUitstroomniveauVSO,
	vakOrder: valuesOf(VAKORDER_ACTIVE.flatMap((active) => VAKORDER_DIRECTIONS.map((direction) => `${active}:${direction}`))),
	verschilbeoogd: VerschilBeoogd,
};

function valuesOf(waarden: readonly (string | number)[]): Record<string, string> {
	return Object.fromEntries(waarden.map((waarde) => [`${waarde}`, `${waarde}`]));
}
